import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, ArrowRight, Share2 } from 'lucide-react';
import { blogPosts as mockPosts } from '../blogData';
import { fetchBlog } from '../api';
import SafeImage from '../components/SafeImage';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSettings } from '../store';
import { applyPageSEO } from '../utils/pageSEO';

const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

const BlogDetail = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const settings = useSettings();
  const [post, setPost] = useState(() => mockPosts.find((p) => p.slug === slug) || null);

  useEffect(() => {
    fetchBlog(slug)
      .then((data) => { if (data && data.slug) setPost(data); })
      .catch(() => {});
  }, [slug]);

  // Update SEO meta tags dynamically (title, description, robots).
  useEffect(() => {
    if (!post) return;
    return applyPageSEO({
      title: post.seo_title || post.title,
      description: post.seo_description || post.excerpt || settings.seo_description,
      noIndex: !!post.seo_no_index,
      noFollow: !!post.seo_no_follow,
    });
  }, [post, settings.seo_description]);

  const related = useMemo(() => {
    if (!post) return [];
    return mockPosts
      .filter((p) => p.slug !== post.slug && (p.category === post.category || (p.tags || []).some((t) => (post.tags || []).includes(t))))
      .slice(0, 3);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen">
        <div className="bg-[#0d64a8] h-24" />
        <div className="max-w-lg mx-auto py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Article not found</h2>
          <p className="text-gray-600 mb-6">The story you’re looking for may have been moved.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 bg-[#0d64a8] text-white px-5 py-2.5 rounded-md">
            <ArrowLeft size={16} /> Back to blog
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard');
      }
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-[#0d64a8]">
        <Header />
        <div className="h-24" />
      </div>

      {/* Hero */}
      <section className="relative h-[420px] overflow-hidden">
        <SafeImage src={post.hero} alt={post.title} fallbackType={post.fbType} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="absolute inset-0 max-w-[900px] mx-auto px-5 lg:px-8 flex flex-col justify-end pb-10">
          <button onClick={() => nav(-1)} className="absolute top-6 left-5 lg:left-8 flex items-center gap-1.5 text-white/90 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2 flex-wrap text-xs text-white/90 mb-3">
            <span className="bg-[#ffcc29] text-[#0a2a4a] px-2 py-0.5 rounded font-semibold">{post.category}</span>
            <span className="flex items-center gap-1"><Calendar size={12}/> {fmtDate(post.date)}</span>
            <span className="flex items-center gap-1"><Clock size={12}/> {post.readTime}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow max-w-4xl leading-tight">{post.title}</h1>
          <div className="text-white/85 text-sm mt-3">by <span className="font-medium text-white">{post.author}</span></div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8">
          <p className="text-lg text-gray-700 leading-relaxed border-l-4 border-[#0d64a8] pl-4 mb-8 italic">
            {post.excerpt}
          </p>

          {post.sections.map((s, i) => (
            <div key={i} className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{s.heading}</h2>
              <p className="text-[16.5px] text-gray-700 leading-[1.85]">{s.body}</p>
            </div>
          ))}

          {/* Tags & share */}
          <div className="border-t border-gray-100 pt-6 mt-10 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Tag size={14} className="text-gray-400" />
              {post.tags.map((t) => (
                <span key={t} className="text-xs bg-[#e8f2fb] text-[#0d64a8] px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>
            <button onClick={handleShare} className="flex items-center gap-1.5 text-sm text-[#0d64a8] hover:underline">
              <Share2 size={14} /> Share article
            </button>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-12 bg-[#f7fafc] border-t border-gray-100">
          <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Keep Reading</h2>
              <Link to="/blog" className="text-sm text-[#0d64a8] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                All articles <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="h-40 relative overflow-hidden">
                    <SafeImage src={p.hero} alt={p.title} fallbackType={p.fbType} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <div className="text-[11px] text-gray-500 mb-1">{p.category} • {p.readTime}</div>
                    <h3 className="font-semibold text-gray-900 text-[15px] line-clamp-2 group-hover:text-[#0d64a8]">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetail;
